// Route-level enforcement of admin page visibility. Before this existed the
// Pages tab was nav decoration: hiding a page removed its menu entry while
// the route stayed fully reachable by URL (2026-08-06 audit D1). The nav
// (Header) and sub-tabs consult the same `pageAccess`; this guard is the
// backstop for direct navigation.
//
// Fail-open while the page config loads (matches the nav — no flicker, no
// lockout on a slow request). A page scoped to roles waits for the role
// instead: deciding before `/api/me` answered read an unknown role as one
// that matches nothing, and redirected a guest's direct link to Welcome.
import type { ReactNode } from 'react';
import { Link, Navigate } from 'react-router';
import { isPageMobile, pageAccess, usePagesConfig } from '@/lib/usePages';
import { useIsMobile } from '@/lib/useIsMobile';
import { useMe } from '@/lib/useMe';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

export function PageGuard({ page, children }: { page: string; children: ReactNode }) {
  const pages = usePagesConfig();
  const me = useMe();
  const isMobile = useIsMobile();
  const access = pageAccess(pages.data, page, me.data?.role);
  if (access === 'hidden') {
    return <Navigate to="/" replace />;
  }
  if (access === 'awaiting-role') {
    if (me.isError) {
      return (
        <div className="container mx-auto max-w-md py-16 text-center">
          <h1 className="text-lg font-semibold text-tres-text-primary">
            Your access could not be confirmed
          </h1>
          <p className="mt-2 text-sm text-tres-text-secondary">{me.error.message}</p>
          <button
            type="button"
            onClick={() => void me.refetch()}
            className={cn(buttonVariants({ variant: 'primary', size: 'lg' }), 'mt-6 inline-block')}
          >
            Try again
          </button>
        </div>
      );
    }
    return (
      <div
        role="status"
        className="container mx-auto max-w-md py-16 text-center text-sm text-tres-text-secondary"
      >
        {me.failureCount > 0 ? 'Confirming your access — retrying…' : 'Confirming your access…'}
      </div>
    );
  }
  // A deep link to a page the registry marks non-mobile gets a plain notice,
  // not a broken render and not a silent redirect — the reader should learn
  // the page exists and where it works.
  if (isMobile && !isPageMobile(pages.data, page)) {
    return (
      <div className="container mx-auto max-w-md py-16 text-center">
        <h1 className="text-lg font-semibold text-tres-text-primary">
          This page needs a larger screen
        </h1>
        <p className="mt-2 text-sm text-tres-text-secondary">
          It is available on a tablet or desktop. On this device you can use the
          pages shown in the menu above.
        </p>
        <Link
          to="/"
          className="mt-6 inline-block rounded-md bg-tres-action px-4 py-2 text-sm font-medium text-white hover:bg-tres-action/90"
        >
          Go to Welcome
        </Link>
      </div>
    );
  }
  return <>{children}</>;
}
